import { Component } from '@theme/component';

/**
 * @extends {Component<{stage: HTMLElement, scenes: HTMLElement[], controls: HTMLElement, previous: HTMLButtonElement, next: HTMLButtonElement, dots: HTMLButtonElement[], count: HTMLElement, dialog: HTMLDialogElement, reader: HTMLElement}>}
 */
class KnHistory extends Component {
  requiredRefs = ['stage', 'controls', 'previous', 'next', 'count', 'dialog', 'reader'];
  /** @type {AbortController | undefined} */
  #controller;
  #index = 0;
  #selectedBlock;
  #total = 0;
  #lastWheel = 0;
  #lockedUntil = 0;
  /** @type {{x: number, y: number} | undefined} */
  #touch;
  /** @type {HTMLElement | undefined} */
  #opener;
  /** @type {{element: HTMLElement, overflow: string}[]} */
  #scrollLocks = [];

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#restoreScroll();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#restoreScroll();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const scenes = this.refs.scenes || [];
    const saved = scenes.findIndex((scene) => scene.dataset.blockId === this.#selectedBlock);
    this.#total = 0;
    this.#touch = undefined;
    this.setAttribute('data-ready', '');
    this.#show(saved >= 0 ? saved : 0);
    this.addEventListener('keydown', this.#onKeyDown, options);
    this.addEventListener('wheel', this.#onWheel, { ...options, passive: false });
    this.addEventListener('touchstart', this.#onTouchStart, { ...options, passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { ...options, passive: true });
    this.addEventListener('touchcancel', () => { this.#touch = undefined; }, options);
    this.refs.dialog.addEventListener('close', this.#onClosed, options);
    window.addEventListener('resize', () => {
      if (this.refs.dialog.open) this.#positionReader();
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      if (!(event.target instanceof Element)) return;
      const index = (this.refs.scenes || []).findIndex((scene) => scene === event.target || scene.contains(event.target));
      if (index < 0) return;
      this.closeReading();
      this.#show(index);
    }, options);
  }

  /** @param {number} index */
  #show(index) {
    const scenes = this.refs.scenes || [];
    this.#index = Math.max(0, Math.min(index, scenes.length - 1));
    this.#selectedBlock = scenes[this.#index]?.dataset.blockId;
    for (const [position, scene] of scenes.entries()) {
      const active = position === this.#index;
      scene.classList.toggle('is-active', active);
      scene.inert = !active;
      scene.setAttribute('aria-hidden', String(!active));
      if (active || position === this.#index + 1) {
        const image = scene.querySelector('img');
        if (image) image.loading = 'eager';
      }
    }
    for (const [position, dot] of (this.refs.dots || []).entries()) dot.setAttribute('aria-current', String(position === this.#index));
    this.refs.previous.disabled = this.#index === 0;
    this.refs.next.disabled = this.#index >= scenes.length - 1;
    this.refs.count.textContent = `${String(this.#index + 1).padStart(2, '0')} / ${String(scenes.length).padStart(2, '0')}`;
    if (document.activeElement instanceof HTMLElement && document.activeElement.closest('article[aria-hidden="true"]')) this.refs.dots?.[this.#index]?.focus({ preventScroll: true });
  }

  previous() { this.#show(this.#index - 1); }
  next() { this.#show(this.#index + 1); }

  /** @param {MouseEvent} event */
  goTo(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-index]');
    if (!(button instanceof HTMLButtonElement)) return;
    const index = Number(button.dataset.index);
    if (Number.isInteger(index)) this.#show(index);
  }

  /** @param {MouseEvent} event */
  openReading(event) {
    const scene = this.refs.scenes?.[this.#index];
    const template = scene?.querySelector('template[data-reading]');
    if (!(template instanceof HTMLTemplateElement) || this.refs.dialog.open) return;
    this.#opener = event.target instanceof Element ? event.target.closest('button') || undefined : undefined;
    this.refs.reader.replaceChildren(template.content.cloneNode(true));
    this.#positionReader();
    for (const element of [document.documentElement, document.querySelector('.page-wrapper')]) {
      if (!(element instanceof HTMLElement)) continue;
      this.#scrollLocks.push({ element, overflow: element.style.overflow });
      element.style.overflow = 'hidden';
    }
    this.refs.dialog.showModal();
    this.refs.dialog.querySelector('button')?.focus({ preventScroll: true });
  }

  #positionReader() {
    const rect = this.refs.stage.getBoundingClientRect();
    const dialog = this.refs.dialog;
    dialog.style.setProperty('--kn-reader-top', `${rect.top}px`);
    // Physical x is converted to the correct logical edge for RTL.
    const left = getComputedStyle(this).direction === 'rtl' ? innerWidth - rect.right : rect.left;
    dialog.style.setProperty('--kn-reader-left', `${left}px`);
    dialog.style.setProperty('--kn-reader-width', `${rect.width}px`);
    dialog.style.setProperty('--kn-reader-height', `${rect.height}px`);
  }

  closeReading() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  #restoreScroll() {
    for (const { element, overflow } of this.#scrollLocks) element.style.overflow = overflow;
    this.#scrollLocks = [];
  }

  #onClosed = () => {
    this.#restoreScroll();
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = undefined;
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (this.refs.dialog.open) {
      if (event.key !== 'Tab') return;
      const items = Array.from(this.refs.dialog.querySelectorAll('button:enabled, a[href], [tabindex="0"]')).filter((element) => element instanceof HTMLElement);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.refs.dialog)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    this.#show(this.#index + direction * (getComputedStyle(this).direction === 'rtl' ? -1 : 1));
  };

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    if (this.refs.dialog.open || event.ctrlKey || matchMedia('(prefers-reduced-motion: reduce), (max-width: 749px)').matches) return;
    const rect = this.getBoundingClientRect();
    if (rect.top < -50 || rect.bottom > innerHeight + 50) return;
    const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerHeight : 1);
    if (Math.abs(delta) < 2 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const direction = Math.sign(delta);
    if ((this.#index === 0 && direction < 0) || (this.#index === (this.refs.scenes?.length || 0) - 1 && direction > 0)) return;
    if (event.cancelable) event.preventDefault();
    const now = performance.now();
    if (now < this.#lockedUntil) return;
    if (now - this.#lastWheel > 200 || Math.sign(this.#total) !== direction) this.#total = 0;
    this.#lastWheel = now;
    this.#total += delta;
    if (Math.abs(this.#total) <= 65) return;
    this.#show(this.#index + direction);
    this.#total = 0;
    this.#lockedUntil = now + 800;
  };

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#touch = !this.refs.dialog.open && event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : undefined;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const start = this.#touch;
    this.#touch = undefined;
    if (!start || this.refs.dialog.open || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) <= 60 || Math.abs(dx) <= Math.abs(dy) * 1.5) return;
    this.#show(this.#index + (dx < 0 ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1));
  };
}

if (!customElements.get('kn-history')) customElements.define('kn-history', KnHistory);
